import { useAdminContext } from "@/providers/AdminProvider";
import {
    BookOpen,
    Bookmark,
    Briefcase,
    Code,
    FolderDot,
    Home,
    LayoutDashboard,
    LogOut,
    Menu,
    School2,
    ShieldCheck,
    Star,
    Trophy,
    User,
} from "lucide-react";
import { signOut } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef } from "react";
import ThemeSelector from "../ui/PrimaryThemeSelector";
import ThemeSwitchButton from "../ui/ThemeSwitchButton";

function AdminSidebar() {
    const { user } = useAdminContext();
    const pathname = usePathname();
    const sidebarRef = useRef<HTMLElement | null>(null);

    const toggleSidebar = useCallback(() => {
        if (window.innerWidth > 768) return;
        sidebarRef.current?.classList.toggle("-translate-x-full");
    }, []);

    const handleScroll = useCallback(() => {
        // Hiding navbar when scrolling
        if (!sidebarRef.current?.classList.contains("-translate-x-full")) {
            toggleSidebar();
        }
    }, [toggleSidebar]);

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [handleScroll]);

    const getLinkStyle = (path: string) => {
        let classNames =
            "border-border-color hover:text-primary w-full border-b px-4 py-3 flex items-center gap-4 ";

        if (path === pathname) {
            classNames += "text-primary";
        }

        return classNames;
    };

    return (
        <aside
            className="fixed left-0 top-0 z-10 h-dvh w-[250px] -translate-x-full bg-background-color transition-all duration-200 md:static md:h-full md:translate-x-0 md:bg-transparent"
            ref={sidebarRef}
        >
            <button
                className="absolute -right-10 top-4 flex size-10 items-center justify-center rounded-r-md border border-l-0 border-border-color bg-background-color outline-none md:hidden"
                onClick={toggleSidebar}
            >
                <Menu className="size-6" />
            </button>
            <div className="flex h-full flex-col overflow-y-auto rounded-none border-r border-border-color bg-card-bg pt-8 md:rounded-2xl md:border-2">
                <ThemeSwitchButton />
                <section className="flex flex-col items-center gap-4 text-center md:text-start">
                    <Image
                        src={user?.image || "/images/profile-image.jpeg"}
                        alt="Image"
                        width={0}
                        height={0}
                        sizes="100vw"
                        className="block w-1/3 min-w-[150px] rounded-full md:min-w-0"
                    />
                    <h1 className="text-2xl font-medium md:text-xl">
                        {user?.name}
                    </h1>
                </section>
                <section className="mt-6 flex flex-col overflow-y-auto border-y border-border-color">
                    <Link
                        href="/admin"
                        className={getLinkStyle("/admin")}
                        onClick={toggleSidebar}
                    >
                        <LayoutDashboard className="size-6" />
                        Dashboard
                    </Link>
                    <Link
                        href="/admin/profile"
                        className={getLinkStyle("/admin/profile")}
                        onClick={toggleSidebar}
                    >
                        <User className="size-6" />
                        Profile
                    </Link>
                    <Link
                        href="/admin/educations"
                        className={getLinkStyle("/admin/educations")}
                        onClick={toggleSidebar}
                    >
                        <School2 className="size-6" />
                        Educations
                    </Link>
                    <Link
                        href="/admin/experiences"
                        className={getLinkStyle("/admin/experiences")}
                        onClick={toggleSidebar}
                    >
                        <Briefcase className="size-6" />
                        Experiences
                    </Link>
                    <Link
                        href="/admin/skills"
                        className={getLinkStyle("/admin/skills")}
                        onClick={toggleSidebar}
                    >
                        <Code className="size-6" />
                        Skills
                    </Link>
                    <Link
                        href="/admin/projects"
                        className={getLinkStyle("/admin/projects")}
                        onClick={toggleSidebar}
                    >
                        <FolderDot className="size-6" />
                        Projects
                    </Link>
                    <Link
                        href="/admin/case-studies"
                        className={getLinkStyle("/admin/case-studies")}
                        onClick={toggleSidebar}
                    >
                        <BookOpen className="size-6" />
                        Case Studies
                    </Link>
                    <Link
                        href="/admin/achievements"
                        className={getLinkStyle("/admin/achievements")}
                        onClick={toggleSidebar}
                    >
                        <Trophy className="size-6" />
                        Achievements
                    </Link>
                    <Link
                        href="/admin/certificates"
                        className={getLinkStyle("/admin/certificates")}
                        onClick={toggleSidebar}
                    >
                        <ShieldCheck className="size-6" />
                        Certificates
                    </Link>
                    <Link
                        href="/admin/testimonials"
                        className={getLinkStyle("/admin/testimonials")}
                        onClick={toggleSidebar}
                    >
                        <Star className="size-6" />
                        Testimonials
                    </Link>
                    <Link
                        href="/admin/resources"
                        className={getLinkStyle("/admin/resources")}
                        onClick={toggleSidebar}
                    >
                        <Bookmark className="size-6" />
                        Resources
                    </Link>
                    <Link
                        href="/"
                        className="flex w-full items-center gap-4 border-b border-border-color px-4 py-3 hover:text-primary"
                    >
                        <Home className="size-6" />
                        Go to Portfolio
                    </Link>
                </section>
                <ThemeSelector />
                <button
                    className="mt-auto flex w-full items-center gap-4 border-t border-border-color px-4 py-3 hover:text-primary"
                    onClick={() => signOut()}
                >
                    <LogOut className="size-6" />
                    Logout
                </button>
            </div>
        </aside>
    );
}
export default AdminSidebar;
